/**
 * Splitting a raster layer into its islands — the pixel side.
 *
 * A pack layer often carries several photo slabs drawn as one layer. The designer wants one layer per slab,
 * which is what Photoshop gives with «Волшебная палочка → Скопировать на новый слой» per slab. Here the layer's
 * alpha decides: every connected opaque region is a piece, and every other non-transparent pixel (soft edge,
 * baked shadow, speck) goes to the piece it is closest to, so the pieces together hold exactly the layer's pixels.
 */

/** Alpha from which a pixel counts as the layer's body; below it is a soft edge or a baked effect. */
export const DECOR_SPLIT_CORE_ALPHA = 128;
/** An opaque region narrower than this on either side is dust, not a piece — it joins the nearest piece. */
export const DECOR_SPLIT_MIN_SIDE = 10;

export interface PixelBounds {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface DecorSplitPiece {
  /** The value of this piece in `labels`; 1-based. */
  readonly label: number;
  /** Every non-transparent pixel of the piece — the box the piece is cut into. */
  readonly bounds: PixelBounds;
  /** The opaque body alone — the layer's shape before soft edges and baked effects. */
  readonly core: PixelBounds;
}

export interface DecorSplitPlan {
  readonly width: number;
  readonly height: number;
  /** Per pixel: the piece label the pixel belongs to, 0 when the layer has no piece at all. */
  readonly labels: Int32Array;
  /** Reading order: rows top to bottom, inside a row left to right. */
  readonly pieces: readonly DecorSplitPiece[];
}

interface MutableBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

function emptyBounds(): MutableBounds {
  return { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
}

function growBounds(b: MutableBounds, x: number, y: number): void {
  if (x < b.minX) b.minX = x;
  if (x > b.maxX) b.maxX = x;
  if (y < b.minY) b.minY = y;
  if (y > b.maxY) b.maxY = y;
}

function toBounds(b: MutableBounds): PixelBounds {
  return { x: b.minX, y: b.minY, width: b.maxX - b.minX + 1, height: b.maxY - b.minY + 1 };
}

/**
 * Plan the split of an RGBA bitmap: opaque 4-connected regions become the pieces, then every remaining pixel is
 * handed to the nearest piece by breadth-first distance. Zero or one piece means the layer is whole.
 */
export function planDecorSplit(rgba: Uint8ClampedArray, width: number, height: number): DecorSplitPlan {
  const total = width * height;
  const labels = new Int32Array(total);
  if (total === 0 || rgba.length < total * 4) return { width, height, labels, pieces: [] };

  const alphaAt = (i: number): number => rgba[i * 4 + 3];
  // One scratch buffer serves both the flood-fill stack and the BFS queue: never both at once.
  const scratch = new Int32Array(total);

  // 1. Opaque bodies, 4-connected; each gets a provisional label and its bounds.
  const cores: MutableBounds[] = [];
  for (let start = 0; start < total; start++) {
    if (labels[start] !== 0 || alphaAt(start) < DECOR_SPLIT_CORE_ALPHA) continue;
    const label = cores.length + 1;
    const bounds = emptyBounds();
    let top = 0;
    scratch[top++] = start;
    labels[start] = label;
    while (top > 0) {
      const i = scratch[--top];
      const x = i % width;
      const y = (i - x) / width;
      growBounds(bounds, x, y);
      const visit = (j: number): void => {
        if (labels[j] === 0 && alphaAt(j) >= DECOR_SPLIT_CORE_ALPHA) {
          labels[j] = label;
          scratch[top++] = j;
        }
      };

      if (x > 0) visit(i - 1);
      if (x + 1 < width) visit(i + 1);
      if (y > 0) visit(i - width);
      if (y + 1 < height) visit(i + width);
    }

    cores.push(bounds);
  }

  // 2. Dust is no piece: bodies under the minimum side lose their label and join a neighbour below.
  const remap = new Int32Array(cores.length + 1);
  const kept: MutableBounds[] = [];
  cores.forEach((bounds, index) => {
    const wide = bounds.maxX - bounds.minX + 1 >= DECOR_SPLIT_MIN_SIDE;
    const tall = bounds.maxY - bounds.minY + 1 >= DECOR_SPLIT_MIN_SIDE;
    if (wide && tall) {
      kept.push(bounds);
      remap[index + 1] = kept.length;
    }
  });
  if (kept.length < 2) {
    for (let i = 0; i < total; i++) labels[i] = remap[labels[i]];
    return { width, height, labels, pieces: kept.length === 0 ? [] : [piece(1, kept[0], rgba, labels, width, height)] };
  }

  // 3. Every other pixel goes to the nearest body: a breadth-first wave from all bodies at once, through
  // transparent pixels too, so a baked shadow shared by two slabs splits along the middle and no pixel is lost.
  let head = 0;
  let tail = 0;
  for (let i = 0; i < total; i++) {
    const label = remap[labels[i]];
    labels[i] = label;
    if (label !== 0) scratch[tail++] = i;
  }

  while (head < tail) {
    const i = scratch[head++];
    const label = labels[i];
    const x = i % width;
    const y = (i - x) / width;
    const claim = (j: number): void => {
      if (labels[j] === 0) {
        labels[j] = label;
        scratch[tail++] = j;
      }
    };

    if (x > 0) claim(i - 1);
    if (x + 1 < width) claim(i + 1);
    if (y > 0) claim(i - width);
    if (y + 1 < height) claim(i + width);
  }

  // 4. Bounds of each piece over its visible pixels only; the cores are already known.
  const visible = kept.map(() => emptyBounds());
  for (let i = 0; i < total; i++) {
    if (alphaAt(i) === 0) continue;
    const x = i % width;
    visible[labels[i] - 1] && growBounds(visible[labels[i] - 1], x, (i - x) / width);
  }

  const pieces = kept.map((core, index) => ({ label: index + 1, bounds: toBounds(visible[index]), core: toBounds(core) }));
  return { width, height, labels, pieces: readingOrder(pieces) };
}

function piece(
  label: number,
  core: MutableBounds,
  rgba: Uint8ClampedArray,
  labels: Int32Array,
  width: number,
  height: number,
): DecorSplitPiece {
  const bounds = emptyBounds();
  for (let i = 0; i < width * height; i++) {
    if (rgba[i * 4 + 3] === 0) continue;
    labels[i] = label;
    const x = i % width;
    growBounds(bounds, x, (i - x) / width);
  }

  return { label, bounds: toBounds(bounds), core: toBounds(core) };
}

/**
 * Rows top to bottom, inside a row left to right — the order the Photoshop script numbers slabs in. Two pieces
 * share a row when their tops differ by less than half the shorter of the two heights.
 */
export function readingOrder(pieces: readonly DecorSplitPiece[]): DecorSplitPiece[] {
  const byTop = [...pieces].sort((a, b) => a.core.y - b.core.y || a.core.x - b.core.x);
  const rows: { top: number; height: number; items: DecorSplitPiece[] }[] = [];
  for (const p of byTop) {
    const row = rows[rows.length - 1];
    if (row && Math.abs(p.core.y - row.top) <= Math.min(p.core.height, row.height) / 2) row.items.push(p);
    else rows.push({ top: p.core.y, height: p.core.height, items: [p] });
  }

  return rows.flatMap(row => row.items.sort((a, b) => a.core.x - b.core.x));
}

/** The piece's own pixels inside its bounds; everything that belongs to another piece stays transparent. */
export function cutDecorPiece(
  rgba: Uint8ClampedArray,
  plan: DecorSplitPlan,
  piece: DecorSplitPiece,
): Uint8ClampedArray<ArrayBuffer> {
  const { x: left, y: top, width, height } = piece.bounds;
  const out = new Uint8ClampedArray(width * height * 4);
  for (let y = 0; y < height; y++) {
    const srcRow = (top + y) * plan.width + left;
    for (let x = 0; x < width; x++) {
      const src = srcRow + x;
      if (plan.labels[src] !== piece.label) continue;
      const s = src * 4;
      const d = (y * width + x) * 4;
      out[d] = rgba[s];
      out[d + 1] = rgba[s + 1];
      out[d + 2] = rgba[s + 2];
      out[d + 3] = rgba[s + 3];
    }
  }

  return out;
}

/** Alpha below this on any pixel of a cut piece makes it a shape rather than a plain rectangle (the frame silhouette's floor). */
export const DECOR_SPLIT_OPAQUE_FLOOR = 250;

/** Whether a cut piece has soft edges or holes — a frame made of it needs the piece's alpha as its shape. */
export function isShapedPiece(rgba: Uint8ClampedArray): boolean {
  for (let i = 3; i < rgba.length; i += 4) if (rgba[i] < DECOR_SPLIT_OPAQUE_FLOOR) return true;
  return false;
}
